import {
    ComponentsProps,
    ComponentsOverrides,
    ComponentsVariants,
    Theme,
    typographyClasses,
    buttonClasses,
    dialogActionsClasses,
} from '@mui/material';
import { outlineOffset } from './utils/focus';

const responsiveButtonStyles = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [`> .${buttonClasses.root}`]: {
        inlineSize: '100%',
    },
};

export const MuiDialog: {
    defaultProps?: ComponentsProps['MuiDialog'];
    styleOverrides?: ComponentsOverrides<Theme>['MuiDialog'];
    variants?: ComponentsVariants['MuiDialog'];
} = {
    defaultProps: {
        fullWidth: true,
        // eslint-disable-next-line local-rules/enforce-logical-css
        maxWidth: 'md',
        scroll: 'body',
    },
    styleOverrides: {
        paperWidthSm: () => ({
            [`> .${dialogActionsClasses.root}`]: responsiveButtonStyles,
        }),
        paper: (props) => ({
            borderRadius: props.theme.borderRadius(props.theme.tokens.borderRadius.md),
            padding: props.theme.spacing(props.theme.tokens.spacing.xl),
            [props.theme.breakpoints.down('sm')]: {
                padding: props.theme.spacing(props.theme.tokens.spacing.md),
            },
        }),
        paperFullScreen: (props) => ({
            borderRadius: props.theme.borderRadius(props.theme.tokens.borderRadius.none),
        }),
    },
};

export const MuiDialogTitle: {
    defaultProps?: ComponentsProps['MuiDialogTitle'];
    styleOverrides?: ComponentsOverrides<Theme>['MuiDialogTitle'];
    variants?: ComponentsVariants['MuiDialogTitle'];
} = {
    defaultProps: {
        variant: 'h2',
        component: 'h2',
    },
    styleOverrides: {
        root: (props) => ({
            padding: props.theme.spacing(
                props.theme.tokens.spacing.none,
                props.theme.tokens.spacing.none,
                props.theme.tokens.spacing.sm,
                props.theme.tokens.spacing.none,
            ),
        }),
    },
};

export const MuiDialogActions: {
    defaultProps?: ComponentsProps['MuiDialogActions'];
    styleOverrides?: ComponentsOverrides<Theme>['MuiDialogActions'];
    variants?: ComponentsVariants['MuiDialogActions'];
} = {
    styleOverrides: {
        root: (props) => ({
            gap: props.theme.spacing(props.theme.tokens.spacing.xs),
            padding: props.theme.spacing(
                props.theme.tokens.spacing.xl,
                props.theme.tokens.spacing.none,
                props.theme.tokens.spacing.none,
            ),
            '>:not(:first-of-type)': {
                marginInlineStart: 'unset',
            },
            [props.theme.breakpoints.down('sm')]: {
                ...responsiveButtonStyles,
                paddingBlockStart: props.theme.spacing(props.theme.tokens.spacing.lg),
            },
        }),
    },
};

const childrenFocusOutlineFix = (props: { theme: Theme }) => ({
    padding: `calc(${outlineOffset} + ${props.theme.tokens.borderWidth.md})`,
    margin: `calc(-1 * (${outlineOffset} + ${props.theme.tokens.borderWidth.md}))`,
});

export const MuiDialogContent: {
    defaultProps?: ComponentsProps['MuiDialogContent'];
    styleOverrides?: ComponentsOverrides<Theme>['MuiDialogContent'];
    variants?: ComponentsVariants['MuiDialogContent'];
} = {
    styleOverrides: {
        root: (props) => [
            childrenFocusOutlineFix(props),
            {
                display: 'flex',
                flexDirection: 'column',
                gap: props.theme.spacing(props.theme.tokens.spacing.lg),
            },
        ],
    },
};

export const MuiDialogContentText: {
    defaultProps?: ComponentsProps['MuiDialogContentText'];
    styleOverrides?: ComponentsOverrides<Theme>['MuiDialogContentText'];
    variants?: ComponentsVariants['MuiDialogContentText'];
} = {
    styleOverrides: {
        root: (props) => ({
            [`&:not(.${typographyClasses.body1})`]: {
                color: props.theme.palette.text.primary,
            },
        }),
    },
};
